import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { getSessionId } from "../lib/session";
import { findHiddenCursor, findHiddenTrail } from "../lib/hiddenCosmetics";
import EventRewardModal from "./EventRewardModal";
import { toast } from "sonner";

/**
 * EventOrchestrator — mounted once at the app root. Owns the visual side-
 * effects for the two glitch events AND surfaces a single shared reward
 * modal whenever a user has something to add to their collection. No
 * persistent HUD boxes — the page itself IS the event.
 *
 * Blackout (18:00–19:00 UTC) → body.blackout-active wallpaper override;
 *                              modal fires when the signed-in user lands
 *                              on /theme-studio inside the window.
 * Royale   (02:00–03:00 UTC) → live cursor + trail hijack via inline css
 *                              and a `vault:trail-override` event; modal
 *                              fires once the window has ended and the
 *                              user is in the 24h claim grace period.
 */
export default function EventOrchestrator() {
  const { user } = useAuth();
  const location = useLocation();
  const [blackout, setBlackout] = useState(null);
  const [royale, setRoyale] = useState(null);
  const [royalePreviewRoll, setRoyalePreviewRoll] = useState(null);
  const [blackoutPreview, setBlackoutPreview] = useState(null);
  const [modal, setModal] = useState(null); // { source, kind, item_id, item_name, card_name? }
  const [busy, setBusy] = useState(false);
  const [alreadyAdded, setAlreadyAdded] = useState(false);
  const dismissedKey = useRef(null); // localStorage key for "user dismissed this event today"

  // ---- Polling ------------------------------------------------------------
  const fetchAll = useCallback(() => {
    const sid = getSessionId();
    api.get("/blackout/status").then((r) => setBlackout(r.data)).catch(() => {});
    api.get("/cursor-royale/status", { headers: { "X-Session-Id": sid } })
      .then((r) => setRoyale(r.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll, user?.id]);
  useEffect(() => {
    const anyActive = blackout?.active || royale?.active;
    const interval = setInterval(fetchAll, anyActive ? 30000 : 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [blackout?.active, royale?.active, fetchAll]);

  // ---- Body classes for the visual effects -------------------------------
  const blackoutActive = !!blackout?.active;
  useEffect(() => {
    if (blackoutActive) document.body.classList.add("blackout-active");
    else document.body.classList.remove("blackout-active");
    return () => document.body.classList.remove("blackout-active");
  }, [blackoutActive]);

  // ---- Cursor Royale cursor + trail hijack -------------------------------
  const royaleActive = !!royale?.active;
  const currentRoll = royale?.current || null;
  useEffect(() => {
    const html = document.documentElement;
    if (royaleActive && currentRoll?.kind === "cursor") {
      const c = findHiddenCursor(currentRoll.item_id);
      if (c?.css) html.style.cursor = c.css;
    }
    return () => { html.style.removeProperty("cursor"); };
  }, [royaleActive, currentRoll?.kind, currentRoll?.item_id]);
  useEffect(() => {
    if (!royaleActive) {
      window.dispatchEvent(new CustomEvent("vault:trail-override", { detail: null }));
      return;
    }
    if (currentRoll?.kind === "trail") {
      const t = findHiddenTrail(currentRoll.item_id);
      if (t?.recipe) window.dispatchEvent(new CustomEvent("vault:trail-override", { detail: t.recipe }));
    } else {
      window.dispatchEvent(new CustomEvent("vault:trail-override", { detail: null }));
    }
    return () => window.dispatchEvent(new CustomEvent("vault:trail-override", { detail: null }));
  }, [royaleActive, currentRoll?.kind, currentRoll?.item_id]);

  // ---- Dismiss memory (per-event, per-day) -------------------------------
  const dismissKeyFor = (source, dateUtc) => `vault.event.dismiss.${source}.${dateUtc}`;
  const isDismissed = (source, dateUtc) => {
    try { return !!localStorage.getItem(dismissKeyFor(source, dateUtc)); } catch { return false; }
  };

  // ---- Reward modal triggers ---------------------------------------------
  // BLACKOUT: signed-in user is inside /theme-studio during the window.
  useEffect(() => {
    if (!user || modal) return;
    if (!blackout?.active) return;
    if (!location.pathname.startsWith("/theme-studio")) return;
    if (blackout.your_state?.claimed_today) return;
    if (isDismissed("blackout", blackout.date_utc)) return;
    let cancelled = false;
    api.get("/blackout/preview").then((r) => {
      if (cancelled) return;
      const d = r.data || {};
      if (d.already_claimed) return;
      if (d.preview) {
        setBlackoutPreview(d.preview);
        setModal({
          source: "blackout",
          kind: d.preview.kind,
          item_id: d.preview.item_id,
          item_name: d.preview.item_name,
        });
        setAlreadyAdded(false);
        dismissedKey.current = dismissKeyFor("blackout", blackout.date_utc);
      } else if (d.fallback_card) {
        setModal({
          source: "blackout",
          kind: "card",
          item_id: null,
          item_name: "Divine Vault Card",
        });
        setAlreadyAdded(false);
        dismissedKey.current = dismissKeyFor("blackout", blackout.date_utc);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user, blackout, location.pathname, modal]);

  // ROYALE: window has ended and user is in the 24h grace + hasn't claimed.
  useEffect(() => {
    if (!user || modal) return;
    if (!royale?.your_state?.can_claim) return;
    if (isDismissed("royale", royale.date_utc)) return;
    const final = royale.final;
    if (!final) return;
    setRoyalePreviewRoll(final);
    setModal({
      source: "royale",
      kind: final.kind,
      item_id: final.item_id,
      item_name: final.item_name,
    });
    setAlreadyAdded(false);
    dismissedKey.current = dismissKeyFor("royale", royale.date_utc);
  }, [user, royale, modal]);

  // ---- Claim handlers ----------------------------------------------------
  const handleAdd = async () => {
    if (!modal) return;
    setBusy(true);
    try {
      let claimResp = null;
      if (modal.source === "blackout") {
        claimResp = await api.post("/blackout/claim");
        const c = claimResp?.data?.claim || {};
        if (c.kind === "card" && c.card_name) {
          setModal((m) => m ? { ...m, kind: "card", item_name: c.card_name } : m);
        }
      } else if (modal.source === "royale") {
        claimResp = await api.post("/cursor-royale/claim");
      }
      setAlreadyAdded(true);
      toast.success("Added to your collection.");
      window.dispatchEvent(new Event("cosmetic-unlocks-changed"));
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (dismissedKey.current) {
      try { localStorage.setItem(dismissedKey.current, "1"); } catch { /* quota */ }
    }
    setModal(null);
    setAlreadyAdded(false);
    setBusy(false);
    dismissedKey.current = null;
  };

  return (
    <EventRewardModal
      open={!!modal}
      reward={modal}
      onAdd={handleAdd}
      onClose={handleClose}
      busy={busy}
      alreadyAdded={alreadyAdded}
    />
  );
}
